import React, { useEffect, useState } from "react";

import BookImage from "../assets/images/book-in-hands.webp";
import { useNavigate } from "react-router-dom";

// Mock PrimaryButton component
const PrimaryButton = ({ label, onClick, disabled, style }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? "#9ca3af" : "#3b82f6",
      color: "white",
      border: "none",
      padding: "12px 24px",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.3s ease",
      boxShadow: disabled ? "none" : "0 4px 12px rgba(59, 130, 246, 0.3)",
      transform: disabled ? "scale(0.98)" : "scale(1)",
      ...style
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.target.style.backgroundColor = "#2563eb";
        e.target.style.transform = "translateY(-2px)";
        e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled) {
        e.target.style.backgroundColor = "#3b82f6";
        e.target.style.transform = "translateY(0)";
        e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
      }
    }}
  >
    {label}
  </button>
);

export const FormPage = () => {

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim().length < 2 ? 'Name must be at least 2 characters long' : '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        return !validateEmail(value) ? 'Please enter a valid email address' : '';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        return !validatePhone(value) ? 'Please enter a valid phone number (10-15 digits)' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'phone') {
      const cleanValue = value.replace(/\D/g, '');
      
      // Format the phone number
      if (cleanValue.length === 0) {
        formattedValue = '';
      } else if (cleanValue.length <= 3) {
        formattedValue = cleanValue;
      } else if (cleanValue.length <= 6) {
        formattedValue = `${cleanValue.slice(0, 3)}-${cleanValue.slice(3)}`;
      } else if (cleanValue.length <= 10) {
        formattedValue = `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 6)}-${cleanValue.slice(6)}`;
      } else {
        // Don't allow more than 10 digits
        return;
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: formattedValue,
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    setTouched({ name: true, email: true, phone: true });

    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store in memory
      window.userData = formData;
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Reset form
        setFormData({ name: "", email: "", phone: "" });
        setTouched({});
        setErrors({});
      }, 3000);

      navigate(-1);

    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputStyle = (fieldName) => ({
    width: "100%",
    maxWidth: "400px",
    padding: "12px 16px",
    borderRadius: "8px",
    border: errors[fieldName] ? "2px solid #ef4444" : touched[fieldName] && !errors[fieldName] ? "2px solid #10b981" : "2px solid #e5e7eb",
    boxSizing: "border-box",
    marginBottom: "8px",
    fontSize: "16px",
    transition: "all 0.3s ease",
    outline: "none",
    backgroundColor: "#fafafa",
  });

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#374151",
    fontSize: "14px",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  };

  if (showSuccess) {
    return (
      <div style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        color: "white"
      }}>
        <div style={{
          textAlign: "center",
          padding: "40px",
          borderRadius: "20px",
          backgroundColor: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(10px)",
          animation: "fadeIn 0.5s ease-in"
        }}>
          <div style={{
            fontSize: "64px",
            marginBottom: "20px",
            animation: "bounce 1s ease-in-out"
          }}>✅</div>
          <h2 style={{ fontSize: "32px", marginBottom: "16px", fontWeight: "700" }}>
            Success!
          </h2>
          <p style={{ fontSize: "18px", opacity: 0.9 }}>
            Your information has been submitted successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
        // padding: "50px",
        boxSizing: "border-box",
        minHeight: "100vh"
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "50px",
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "40px",
          transition: "transform 0.3s ease",
        }}
      >
        <div
          style={{
            flex: "1 1 300px",
            aspectRatio: "4 / 3",
            borderRadius: "16px",
            overflow: "hidden",
            position: "relative",
            transform: "scale(1)",
            transition: "transform 0.3s ease",
            cursor: "pointer"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.02)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <img
            style={{ 
              width: "100%", 
              height: "100%", 
              objectFit: "cover",
              filter: "brightness(1.1) contrast(1.1)"
            }}
            src={BookImage}
            alt="Book opened in hands"
          />
          <div style={{
            position: "absolute",
            bottom: "0",
            left: "0",
            right: "0",
            background: "linear-gradient(transparent, rgba(0,0,0,0.3))",
            height: "40%"
          }} />
        </div>
        
        <div
          style={{
            flex: "1 1 300px",
            gap: "20px",
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <p style={{ 
              fontSize: "14px", 
              textAlign: "left",
              color: "#6366f1",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "8px"
            }}>
              Complete Your Profile
            </p>
            <h1
              style={{
                fontSize: "36px",
                textAlign: "left",
                lineHeight: "1.2",
                margin: "0",
                background: "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: "700"
              }}
            >
              Welcome! Please Share Your Details to Get Started
            </h1>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ position: "relative" }}>
              <label style={labelStyle}>
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={(e) => {
                  e.target.style.borderColor = errors.name ? "#ef4444" : "#3b82f6";
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                }}
                onBlurCapture={(e) => {
                  handleBlur(e);
                  e.target.style.borderColor = errors.name ? "#ef4444" : touched.name && !errors.name ? "#10b981" : "#e5e7eb";
                  e.target.style.backgroundColor = "#fafafa";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="Enter your full name"
                style={getInputStyle('name')}
                disabled={isSubmitting}
              />
              {errors.name && touched.name && (
                <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                  ❌ {errors.name}
                </p>
              )}
              {!errors.name && touched.name && formData.name && (
                <p style={{ color: "#10b981", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                  ✅ Looks good!
                </p>
              )}
            </div>

            <div style={{ position: "relative" }}>
              <label style={labelStyle}>
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={(e) => {
                  e.target.style.borderColor = errors.phone ? "#ef4444" : "#3b82f6";
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                }}
                onBlurCapture={(e) => {
                  handleBlur(e);
                  e.target.style.borderColor = errors.phone ? "#ef4444" : touched.phone && !errors.phone ? "#10b981" : "#e5e7eb";
                  e.target.style.backgroundColor = "#fafafa";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="123-456-7890"
                style={getInputStyle('phone')}
                disabled={isSubmitting}
              />
              {errors.phone && touched.phone && (
                <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                  ❌ {errors.phone}
                </p>
              )}
              {!errors.phone && touched.phone && formData.phone && (
                <p style={{ color: "#10b981", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                  ✅ Valid phone number!
                </p>
              )}
            </div>

            <div style={{ position: "relative" }}>
              <label style={labelStyle}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={(e) => {
                  e.target.style.borderColor = errors.email ? "#ef4444" : "#3b82f6";
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                }}
                onBlurCapture={(e) => {
                  handleBlur(e);
                  e.target.style.borderColor = errors.email ? "#ef4444" : touched.email && !errors.email ? "#10b981" : "#e5e7eb";
                  e.target.style.backgroundColor = "#fafafa";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="Enter your email address"
                style={getInputStyle('email')}
                disabled={isSubmitting}
              />
              {errors.email && touched.email && (
                <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                  ❌ {errors.email}
                </p>
              )}
              {!errors.email && touched.email && formData.email && (
                <p style={{ color: "#10b981", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                  ✅ Valid email address!
                </p>
              )}
            </div>

            <div style={{ marginTop: "10px" }}>
              <PrimaryButton 
                label={isSubmitting ? "Submitting..." : "Submit"} 
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  transform: isSubmitting ? "scale(0.98)" : "scale(1)",
                  transition: "all 0.2s ease"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};